import type { CellTemplateProp, HyperFunc, VNode } from '@revolist/react-datagrid';

import {
  FIELD_HEIGHT,
  SelectOptionsSource,
  getSelectOptionLabel,
  resolveSelectOptions,
} from './SelectCellEditor';
import { RevoGridTableExtras } from './types';

export const createSelectCellTemplate =
  (options: SelectOptionsSource) =>
  (h: HyperFunc<VNode>, props: CellTemplateProp, extra?: RevoGridTableExtras) => {
    const field = extra?.field;
    const resolvedOptions = resolveSelectOptions(options, props.model);
    // An empty resolved list means there is nothing to offer yet and 1 is the only option
    const isDisabled = resolvedOptions.length === 0 || resolvedOptions.length === 1;

    return h(
      'span',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        },
      },
      h(
        'div',
        {
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            width: '100%',
            height: field?.height ?? `${FIELD_HEIGHT}px`,
            // MUI OutlinedInput small: 14px left gutter, icon sits at right: 7px
            padding: '0 7px 0 14px',
            border: `1px solid ${field?.border ?? 'transparent'}`,
            borderRadius: field?.radius ?? '8px',
            backgroundColor: field?.bg ?? 'transparent',
            color: field?.text ?? 'inherit',
            fontSize: field?.fontSize ?? 'inherit',
            opacity: isDisabled ? 0.5 : 1,
          },
        },
        // children go in as an array: HyperFunc has no varargs overload
        [
          h(
            'span',
            { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            getSelectOptionLabel(resolvedOptions, props.model?.[props.prop])
          ),
          h(
            'span',
            {
              role: 'button',
              'aria-label': 'Open dropdown',
              style: {
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                cursor: isDisabled ? 'default' : 'pointer',
                color: field?.icon ?? 'currentColor',
              },
              // do not let the chevron start a range drag
              onMouseDown: (event: MouseEvent) => event.stopPropagation(),
              onClick: (event: MouseEvent) => {
                event.stopPropagation();
                // readonly already keeps double-click/keyboard entry out; this
                // guards the chevron's own direct `setCellEdit` call, which
                // bypasses that check.
                if (!isDisabled) extra?.onOpenEditor(props.rowIndex, props.prop);
              },
            },
            h(
              'svg',
              { width: '24', height: '24', viewBox: '0 0 24 24', 'aria-hidden': 'true' },
              h('path', { fill: 'currentColor', d: 'M7 10l5 5 5-5z' })
            )
          ),
        ]
      )
    );
  };
